import { ReactNode, useEffect, useState } from 'react';
import AddExistingBooking from './AddExistingBooking';
import { AddExistingBookingProps } from '@msc-b2c/interfaces/types';
import {
  getActiveSession,
  GetAllBookingsMyMSC,
  getCountryCodeISO3,
  RootState,
} from '@msc-b2c/shared-utils';
import { useSelector } from 'react-redux';
import { AllBooking } from '@msc-b2c/interfaces/interfaces';

export function AddExistingBookingWrapper({
  addExistingBookingLabel,
  addExistingBookingDescription,
  missingCruiseButtonLabel,
  missingCruiseButtonStyle,
  missingCruiseButtonSize,
  bookingNumberInputPlaceholder,
  validationRulesContainerBookingNumber,
  addBookingButtonLabel,
  addBookingButtonStyle,
  addBookingButtonSize,
  successIcon,
  successModalTitle,
  successModalText,
  errorModalTitle,
  errorModalText,
  continueCtaLabel,
  initialCTAStateForced,
}: Readonly<AddExistingBookingProps>): ReactNode {
  const AEMprops = {
    addExistingBookingLabel,
    addExistingBookingDescription,
    missingCruiseButtonLabel,
    missingCruiseButtonStyle,
    missingCruiseButtonSize,
    bookingNumberInputPlaceholder,
    validationRulesContainerBookingNumber,
    addBookingButtonLabel,
    addBookingButtonStyle,
    addBookingButtonSize,
    successIcon,
    successModalTitle,
    successModalText,
    errorModalTitle,
    errorModalText,
    continueCtaLabel,
    initialCTAStateForced,
  };

  const { email } = getActiveSession();
  const [bookings, setBookings] = useState<AllBooking[] | null | undefined>(undefined);
  const allBookings = useSelector((state: RootState) => state.myCruise.allBookings);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // If the CTA is explicitly forced, it must always be displayed.
    // In this case, we do not need to check the user’s bookings.
    if (initialCTAStateForced) {
      setIsLoading(false);

      return;
    }

    // If bookings are already available (because a parent component
    // has already loaded them — e.g. in "plan-my-cruise" pages),
    // we simply reuse that data and avoid loading them again.
    if (allBookings) {
      setBookings(allBookings);
      setIsLoading(false);

      return;
    }

    // If bookings are not available, it means they have not been loaded yet
    // (e.g. in "cruise-details" pages).
    // We need to retrieve them in order to determine the correct CTA state.
    const fetchBookings = async () => {
      const getBooks = new GetAllBookingsMyMSC(false, { email });
      const getBooksData = await getBooks.getData();
      const filteredBookings = getBooksData?.bookings?.filter(
        (booking) => booking.marketCode === getCountryCodeISO3()
      );
      setBookings(filteredBookings);
      setIsLoading(false);
    };
    fetchBookings();
  }, [allBookings, initialCTAStateForced]);

  return isLoading ? null : (
    <AddExistingBooking
      bookings={bookings}
      {...AEMprops}
    />
  );
}

export default AddExistingBookingWrapper;

AddExistingBookingWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/addexistingbooking';
